import LocalStorageService from "./local_storage_service";
import SessionStorageService from "./session_storage_service";
import FirebaseNotificationService from "../firebase";
import BirthDates from "../../models/static/birthdates";
import DateUtils from "../date-utils";
import BroadcastService from "../broadcast";
import Api from "../api/api";
import AuthBlockerService from "../auth-blocker";

const initialNexusSearchConfigs = {
    canToggleAdvancedMode: false,
    canSeeAutocompleteSuggestions: false,
    sidebarFacetsCollapsedByDefault: true,
    hidePropertyGroupFacetMostFrequentValues: true,
}

export interface AuthNavigation {
    loginUrl: string;
    logoutUrl: string;
    url: string;
    version: string;
}

/**
 * Service that communicates with the local storage of the window
 */
export default class CacheService {

    private static dispatchEvent(key: string, {newValue, oldValue}: { newValue: any, oldValue?: any }) {
        window.dispatchEvent(new StorageEvent('storage', {
            key,
            newValue: newValue,
            oldValue: oldValue,
            storageArea: localStorage,
        }))
    }

    // ################################         Auto Login Info        ################################


    /**
     * Sets the login information to the local storage of the window
     * @param token {string}
     * @param tokenLifeSpanMinutes {number} the number of minutes until the token expires
     */
    static setAutoInfo(token: string, tokenLifeSpanMinutes: number) {
        const existingToken = LocalStorageService.get(LocalStorageService.keys.token);
        LocalStorageService.set(LocalStorageService.keys.token, token);
        LocalStorageService.set(LocalStorageService.keys.tokenExpiration, DateUtils.localNow().add(tokenLifeSpanMinutes, 'minute').valueOf().toString());
        AuthBlockerService.acceptToken(token);
        this.dispatchEvent(LocalStorageService.keys.token, {newValue: token, oldValue: existingToken});
    }

    /**
     * Checks to see if the user has a token and their expiry time has not passed
     */
    static isLoggedIn(): boolean {
        const tokenAndExpiration = this.retrieveTokenAndExpiration();
        if (!tokenAndExpiration)
            return false;
        return tokenAndExpiration.tokenExpiration > 0;
    }

    /**
     * Retrieves the token and expiry time in minutes form the local storage.
     */
    static retrieveTokenAndExpiration() {
        let token = LocalStorageService.get(LocalStorageService.keys.token);
        let expDateString = LocalStorageService.get(LocalStorageService.keys.tokenExpiration);
        if (isNaN(parseInt(expDateString))) {
            return undefined;
        }
        let expDate = DateUtils.local(parseInt(expDateString ?? '0'));
        let currentDate = DateUtils.localNow();
        if (!token || !expDate || currentDate.isSameOrAfter(expDate)) {
            return undefined;
        }
        return {token, tokenExpiration: DateUtils.instance.duration(expDate.diff(currentDate)).asMinutes()};
    };

    /**
     * Logs the operator out the system by removing the operator from the local storage
     */
    static async logout(deactivateNotificationService = true) {
        if (deactivateNotificationService) {
            await FirebaseNotificationService.deactivateDevice(false);
        }
        for (const key of LocalStorageService.getAllKeys(true)) {
            LocalStorageService.remove(key);
        }
        SessionStorageService.removeAll();
        this.dispatchEvent(LocalStorageService.eventKeys.logout, {newValue: null});
    }

    // ################################         Print Application Info        ################################

    /**
     * Gets the print application token
     */
    static get printApplicationToken() {
        return LocalStorageService.get(LocalStorageService.keys.printApplicationToken);
    }

    /**
     * Sets the print application token
     * @param token {string} the token
     */
    static setPrintApplicationToken(token?: string) {
        const prevValue = LocalStorageService.get(LocalStorageService.keys.printApplicationToken);
        if (!token) {
            LocalStorageService.remove(LocalStorageService.keys.printApplicationToken);
        } else {
            LocalStorageService.set(LocalStorageService.keys.printApplicationToken, token);
        }
        this.dispatchEvent(LocalStorageService.keys.printApplicationToken, {newValue: token, oldValue: prevValue});
    }





    // ################################         CACHED URL        ################################

    /**
     * Sets the core apis for login / logout in local storage of the window
     * @param loginUrl {string}
     * @param logoutUrl {string}
     */
    static setUrlConfig(loginUrl: string, logoutUrl: string): AuthNavigation | null {
        const replaceUrl = "https://core.bizkeytech.com";
        const replacement = "https://bizcore.bizkeytech.com";
        if (loginUrl.startsWith(replaceUrl)) {
            loginUrl = loginUrl.replace(replaceUrl, replacement);
        }
        if (logoutUrl.startsWith(replaceUrl)) {
            logoutUrl = logoutUrl.replace(replaceUrl, replacement);
        }
        const url = loginUrl.substring(0, loginUrl.toLowerCase().indexOf("/login"));
        LocalStorageService.set(LocalStorageService.keys.authNavigation, JSON.stringify({
            loginUrl: loginUrl,
            logoutUrl: logoutUrl,
            url: url,
            version: '1.0.0',
        }))
        return CacheService.getUrlConfig();
    }

    /**
     * Fetches the login and logout url of the core from local storage of the window.
     * If the config is not found or the version is different than the current one, it returns null.
     */
    static getUrlConfig(): AuthNavigation | null {
        const currentAuthNavVersion = '1.0.0';
        const urlConfig = LocalStorageService.get(LocalStorageService.keys.authNavigation);
        if (!urlConfig) {
            return null;
        }
        try {
            const parsed = JSON.parse(urlConfig);
            const sameAsCurrentVersion = parsed?.version === currentAuthNavVersion;
            if (!sameAsCurrentVersion) {
                LocalStorageService.remove(LocalStorageService.keys.authNavigation);
                return null;
            }
            return parsed as AuthNavigation;
        } catch (e) {
            LocalStorageService.remove(LocalStorageService.keys.authNavigation);
            return null;
        }
    }

    /**
     * Gets the login and logout url of the core from local storage
     */
    static async getOrFetchUrlConfig(): Promise<AuthNavigation | null> {
        const urlConfig = CacheService.getUrlConfig();
        if (urlConfig) {
            return urlConfig;
        }
        const response = await Api.getApplicationConfigurations();
        if (response?.resultFlag && response.data) {
            return CacheService.setUrlConfig(response.data.loginUrl, response.data.logoutUrl);
        }
        return null;
        // TODO: uncomment when developing local auth checks.
        // return {
        //     loginUrl: "http://localhost:3002/login",
        //     logoutUrl: "http://localhost:3002/logout",
        //     url: "http://localhost:3002",
        //     version: "1.0.0",
        // }
    }

    // ################################         DIALOGS        ################################

    /**
     * Fetches whether the user is currently viewing the whats-new dialog.
     * @return {boolean}
     */
    static get canOpenWhatsNewDialog(): boolean {
        const timestamp = LocalStorageService.get(LocalStorageService.keys.readNewVersionsTimestamp);
        if (!timestamp) {
            return true;
        }
        const endOfToday = DateUtils.local().endOf('day');
        const endOfTimestampDay = DateUtils.local(parseInt(timestamp)).endOf('day');
        return endOfToday.isAfter(endOfTimestampDay)
    }

    /**
     * Sets the flag for the reading the whats-new-dialog and emits an event to close all the dialogs in the other tabs.
     */
    static whatsNewDialogToggled(open: boolean, emitEvent: boolean = false) {
        const value = `${open}`;

        if (open) {
            LocalStorageService.set(LocalStorageService.keys.readNewVersionsTimestamp, DateUtils.localNow().valueOf().toString());
        }

        if (emitEvent) {
            window.dispatchEvent(new StorageEvent('storage', {
                key: LocalStorageService.eventKeys.whatsNewDialog,
                newValue: value,
                storageArea: localStorage,
            }))
        }
    }

    /**
     * Fetches whether the operator should see the welcome to panel dialog.
     */
    static get canOpenWelcomeToPanelDialog(): boolean {
        const timestamp = LocalStorageService.get(LocalStorageService.keys.seenWelcomeToPanelDialogTimestamp);
        if (!timestamp) {
            return true;
        }
        const endOfToday = DateUtils.local().endOf('day');
        const endOfTimestampDay = DateUtils.local(parseInt(timestamp)).endOf('day');
        return endOfToday.isAfter(endOfTimestampDay)
    }

    /**
     * Sets the flag for the reading the whats-new-dialog and emits an event to close all the dialogs in the other tabs.
     */
    static welcomeToPanelDialogToggled(open: boolean, emitEvent: boolean = false) {
        const value = `${open}`;

        if (open) {
            LocalStorageService.set(LocalStorageService.keys.seenWelcomeToPanelDialogTimestamp, DateUtils.localNow().valueOf().toString());
        }

        if (emitEvent) {
            window.dispatchEvent(new StorageEvent('storage', {
                key: LocalStorageService.eventKeys.welcomeToPanelDialog,
                newValue: value,
                storageArea: localStorage,
            }))
        }
    }

    /**
     * Fetches whether the operator should see their birthday dialog.
     * @return {boolean}
     */
    static get shouldOpenBirthdayDialog(): boolean {
        let data = LocalStorageService.get(LocalStorageService.keys.seenHappyBirthdayDialog);
        const operatorId = this.getOperatorId();
        if (!operatorId)
            return false;
        if (!data) {
            data = JSON.stringify({
                [operatorId]: false
            })
            LocalStorageService.set(LocalStorageService.keys.seenHappyBirthdayDialog, data)
        }
        data = JSON.parse(data);
        if (!data.hasOwnProperty(operatorId)) {
            data = JSON.stringify({
                ...data,
                [operatorId]: false,
            })
            LocalStorageService.set(LocalStorageService.keys.seenHappyBirthdayDialog, data)
        }
        const operatorBirthDay = BirthDates.getBirthDate(operatorId);
        if (!operatorBirthDay) {
            return false;
        }
        if (!!data[operatorId] && data[operatorId] >= DateUtils.local().year()) {
            return false;
        }
        const birthdayThisYear = DateUtils.local(operatorBirthDay).year(DateUtils.local().year());
        const shouldOpen = DateUtils.local(birthdayThisYear).isSame(DateUtils.localNow(), 'day');
        if (shouldOpen) {
            LocalStorageService.set(LocalStorageService.keys.seenHappyBirthdayDialog, JSON.stringify({
                ...data,
                [operatorId]: DateUtils.local().year()
            }))
        }
        return shouldOpen;
    }

    // ################################         Search Schema        ################################

    /**
     * Fetches the value of the search schema key.
     */
    public static getSearchSchema() {
        const searchSchema = LocalStorageService.get(LocalStorageService.keys.searchSchema);
        try {
            return JSON.parse(searchSchema);
        } catch (e) {
            return null;
        }
    }

    /**
     * Sets the value of the search schema key.
     * @param value
     */
    public static setSearchSchema(value: any) {
        return LocalStorageService.set(LocalStorageService.keys.searchSchema, JSON.stringify(value));
    }

    // ################################         Industry Property Groups        ################################

    /**
     * Fetches the value of the industry property groups key.
     */
    public static getIndustryPropertyGroups() {
        const searchSchema = LocalStorageService.get(LocalStorageService.keys.industryPropertyGroups);
        try {
            return JSON.parse(searchSchema);
        } catch (e) {
            return null;
        }
    }

    /**
     * Sets the value of the industry property groups key.
     * @param value
     */
    public static setIndustryPropertyGroups(value: any) {
        return LocalStorageService.set(LocalStorageService.keys.industryPropertyGroups, JSON.stringify(value));
    }

    // ################################         Industry Property Group Attributes        ################################

    /**
     * Fetches the value of the industry property groups key.
     */
    public static getIndustryPropertyGroupAttributes() {
        const searchSchema = LocalStorageService.get(LocalStorageService.keys.industryPropertyGroupAttributes);
        try {
            return JSON.parse(searchSchema);
        } catch (e) {
            return null;
        }
    }

    /**
     * Sets the value of the industry property groups key.
     * @param value
     */
    public static setIndustryPropertyGroupAttributes(value: any) {
        return LocalStorageService.set(LocalStorageService.keys.industryPropertyGroupAttributes, JSON.stringify(value));
    }

    // ################################         Price Level Dictionary        ################################

    /**
     * Fetches the value of the industry property groups key.
     */
    public static getPriceLevelDictionary() {
        const searchSchema = LocalStorageService.get(LocalStorageService.keys.priceLevelDictionary);
        try {
            return JSON.parse(searchSchema);
        } catch (e) {
            return null;
        }
    }

    /**
     * Sets the value of the industry property groups key.
     * @param value
     */
    public static setPriceLevelDictionary(value: any) {
        if (!value) {
            return LocalStorageService.remove(LocalStorageService.keys.priceLevelDictionary);
        }
        return LocalStorageService.set(LocalStorageService.keys.priceLevelDictionary, JSON.stringify(value));
    }

    // ################################         Nexus search config        ################################

    /**
     * Fetches the nexus search configs from the local storage
     */
    static getNexusSearchConfigs() {
        const searchSchema = LocalStorageService.get(LocalStorageService.keys.nexusSearchConfigs);
        try {
            const saved = JSON.parse(searchSchema);
            return {
                ...initialNexusSearchConfigs,
                ...saved,
            };
        } catch (e) {
            return initialNexusSearchConfigs
        }
    }

    /**
     * Sets the nexus search configs to the local storage
     * @param data
     */
    static setNexusSearchConfigs(data: Record<string, boolean>) {
        const config = {
            ...initialNexusSearchConfigs,
            ...(data ?? {}),
        };
        LocalStorageService.set(LocalStorageService.keys.nexusSearchConfigs, JSON.stringify(config));
        BroadcastService.postMessage(BroadcastService.channelNames.nexusSearchConfigs, config);
    }

    // ################################         App-bar search mode        ################################

    /**
     * Reads the last-known app-bar search mode from local storage for immediate UI hydrate.
     */
    static getAppBarSearchMode(): 'nexus' | 'system' | null {
        const value = LocalStorageService.get(LocalStorageService.keys.appBarSearchMode);
        if (value === 'nexus' || value === 'system') {
            return value;
        }
        return null;
    }

    /**
     * Mirrors the operator's app-bar search mode into local storage for fast subsequent loads.
     */
    static setAppBarSearchMode(mode: 'nexus' | 'system') {
        if (mode !== 'nexus' && mode !== 'system') {
            return;
        }
        const prevValue = LocalStorageService.get(LocalStorageService.keys.appBarSearchMode);
        LocalStorageService.set(LocalStorageService.keys.appBarSearchMode, mode);
        this.dispatchEvent(LocalStorageService.keys.appBarSearchMode, {newValue: mode, oldValue: prevValue});
    }

    // ################################         Operator Partner ID        ################################

    /**
     * Fetches the operator partner id from the local storage
     */
    static getOperatorPartnerId() {
        return LocalStorageService.get(LocalStorageService.keys.operatorPartnerId);
    }

    /**
     * Sets the operator partner id to the local storage
     * @param value
     */
    static setOperatorPartnerId(value: string | undefined) {
        const prevValue = LocalStorageService.get(LocalStorageService.keys.operatorPartnerId);
        if (!value) {
            LocalStorageService.remove(LocalStorageService.keys.operatorPartnerId);
        } else {
            LocalStorageService.set(LocalStorageService.keys.operatorPartnerId, value);
        }
        this.dispatchEvent(LocalStorageService.keys.operatorPartnerId, {newValue: value, oldValue: prevValue});
    }

    // ################################         Operator ID        ################################


    /**
     * Fetches the operator id from the local storage
     */
    static getOperatorId() {
        return LocalStorageService.get(LocalStorageService.keys.operatorId);
    }

    /**
     * Sets the operator id to the local storage and emits an event to update it in the other tabs. If the value is undefined, removes it from the local storage.
     * @param value
     */
    static setOperatorId(value: string | undefined) {
        const prevValue = LocalStorageService.get(LocalStorageService.keys.operatorId);
        if (!value) {
            LocalStorageService.remove(LocalStorageService.keys.operatorId);
        } else {
            LocalStorageService.set(LocalStorageService.keys.operatorId, value);
        }
        this.dispatchEvent(LocalStorageService.keys.operatorId, {newValue: value, oldValue: prevValue});
    }

    // ################################         External Application IDs        ################################

    /**
     * Fetches the biz task service application id from the local storage. This can be used by external applications to identify themselves and receive the relevant notifications. If the value is not found, returns undefined.
     */
    static get bizTaskServiceApplicationId() {
        return LocalStorageService.get(LocalStorageService.keys.bizTaskServiceApplicationId);
    }

    /**
     * Sets the biz task service application id to the local storage and emits an event to update it in the other tabs. If the value is undefined, removes it from the local storage.
     * @param value
     */
    static setBizTaskServiceApplicationId(value?: string) {
        const prevValue = LocalStorageService.get(LocalStorageService.keys.bizTaskServiceApplicationId);
        if (!value) {
            LocalStorageService.remove(LocalStorageService.keys.bizTaskServiceApplicationId);
        } else {
            LocalStorageService.set(LocalStorageService.keys.bizTaskServiceApplicationId, value);
        }
        this.dispatchEvent(LocalStorageService.keys.bizTaskServiceApplicationId, {newValue: value, oldValue: prevValue});
    }

    /**
     * Fetches the biz note service application id from the local storage. This can be used by external applications to identify themselves and receive the relevant notifications. If the value is not found, returns undefined.
     */
    static get bizNoteServiceApplicationId() {
        return LocalStorageService.get(LocalStorageService.keys.bizNoteServiceApplicationId);
    }

    /**
     * Sets the biz note service application id to the local storage and emits an event to update it in the other tabs. If the value is undefined, removes it from the local storage.
     * @param value
     */
    static setBizNoteServiceApplicationId(value?: string) {
        const prevValue = LocalStorageService.get(LocalStorageService.keys.bizNoteServiceApplicationId);
        if (!value) {
            LocalStorageService.remove(LocalStorageService.keys.bizNoteServiceApplicationId);
        } else {
            LocalStorageService.set(LocalStorageService.keys.bizNoteServiceApplicationId, value);
        }
        this.dispatchEvent(LocalStorageService.keys.bizNoteServiceApplicationId, {newValue: value, oldValue: prevValue});
    }

    // ################################         Operator Image URL        ################################

    /**
     * Fetches the operator profile image base url from the local storage. The full url can be constructed by concatenating the base url with the operator id. If the value is not found, returns undefined.
     */
    static get operatorProfileImageBaseUrl() {
        return LocalStorageService.get(LocalStorageService.keys.operatorImageProfileBaseUrl);
    }

    /**
     * Sets the operator profile image base url to the local storage and emits an event to update it in the other tabs. The full url can be constructed by concatenating the base url with the operator id. If the value is undefined, removes it from the local storage.
     * @param value
     */
    static setOperatorProfileImageBaseUrl(value?: string) {
        const prevValue = LocalStorageService.get(LocalStorageService.keys.operatorImageProfileBaseUrl);
        if (!value) {
            LocalStorageService.remove(LocalStorageService.keys.operatorImageProfileBaseUrl);
        } else {
            LocalStorageService.set(LocalStorageService.keys.operatorImageProfileBaseUrl, value);
        }
        this.dispatchEvent(LocalStorageService.keys.operatorImageProfileBaseUrl, {newValue: value, oldValue: prevValue});
    }

    /**
     * Gets the operator profile image url by concatenating the base url with the operator id. If either of them is not found, returns undefined.
     * @param operatorId
     */
    static getOperatorProfileImage(operatorId?: string) {
        return this.operatorProfileImageBaseUrl && operatorId ? `${this.operatorProfileImageBaseUrl}${operatorId}` : undefined;
    }

    // ################################         Notification token        ################################

    /**
     * Fetches the notification token from the local storage
     */
    static get notificationToken() {
        return LocalStorageService.get(LocalStorageService.keys.notificationToken);
    }

    /**
     * Sets the notification token to the local storage and emits an event to update it in the other tabs.
     * @param value
     */
    static setNotificationToken(value?: string) {
        const prevValue = LocalStorageService.get(LocalStorageService.keys.notificationToken);
        if (!value) {
            LocalStorageService.remove(LocalStorageService.keys.notificationToken);
        } else {
            LocalStorageService.set(LocalStorageService.keys.notificationToken, value);
        }
        this.dispatchEvent(LocalStorageService.keys.notificationToken, {newValue: value, oldValue: prevValue});
    }

    // ################################         Application Version        ################################

    /**
     * Fetches the application version from local storage. This can be used to trigger updates across tabs when a new version is released.
     */
    static get appVersion() {
        return LocalStorageService.get(LocalStorageService.keys.appVersion);
    }

    /**
     * Sets the application version in local storage. This can be used to trigger updates across tabs when a new version is released.
     * @param value
     */
    static setAppVersion(value?: string) {
        const prevValue = LocalStorageService.get(LocalStorageService.keys.appVersion);
        if (!value) {
            LocalStorageService.remove(LocalStorageService.keys.appVersion);
        } else {
            LocalStorageService.set(LocalStorageService.keys.appVersion, value);
        }
        this.dispatchEvent(LocalStorageService.keys.appVersion, {newValue: value, oldValue: prevValue});
    }
}
